// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Live event trail.
 *
 * Serves the same purpose as the log in Dropwell: a claim that nothing is
 * uploaded is worth more when you can watch each local step happen and see that
 * none of them is a network call.
 */

export type EventCategory = 'system' | 'file' | 'parse' | 'mesh' | 'view' | 'export';
export type EventLevel = 'info' | 'ok' | 'warn' | 'err';

export interface LogEvent {
  ts: number;
  cat: EventCategory;
  level: EventLevel;
  msg: string;
}

const MAX_EVENTS = 500;

const events: LogEvent[] = [];
const listeners: Array<(e: LogEvent) => void> = [];
let listEl: HTMLElement | null = null;
let countEl: HTMLElement | null = null;

export function emit(cat: EventCategory, level: EventLevel, msg: string): void {
  const event: LogEvent = { ts: Date.now(), cat, level, msg };
  events.push(event);
  if (events.length > MAX_EVENTS) {
    events.shift();
    listEl?.querySelector('.ev')?.remove();
  }
  for (const listener of listeners) listener(event);
}

export function mountEventDrawer(container: HTMLElement): void {
  container.innerHTML = '';

  const bar = document.createElement('div');
  bar.className = 'drawer-bar';
  const count = document.createElement('span');
  count.className = 'drawer-count';
  count.textContent = `${events.length} events`;
  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'drawer-clear';
  clear.textContent = 'Clear';
  clear.addEventListener('click', () => {
    events.length = 0;
    if (listEl) listEl.innerHTML = '';
    count.textContent = '0 events';
  });
  bar.append(count, clear);

  const list = document.createElement('div');
  list.className = 'drawer-list';

  container.append(bar, list);
  listEl = list;
  countEl = count;

  for (const event of events) append(event);
  listeners.push((event) => {
    append(event);
    if (countEl) countEl.textContent = `${events.length} events`;
  });
  if (countEl) countEl.textContent = `${events.length} events`;
}

function append(event: LogEvent): void {
  if (!listEl) return;
  const atBottom = listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 40;

  const row = document.createElement('div');
  row.className = 'ev';
  row.dataset.level = event.level;

  const ts = document.createElement('span');
  ts.className = 'ev-ts';
  ts.textContent = formatTime(event.ts);
  const cat = document.createElement('span');
  cat.className = 'ev-cat';
  cat.textContent = event.cat;
  const msg = document.createElement('span');
  msg.className = 'ev-msg';
  msg.textContent = event.msg;

  row.append(ts, cat, msg);
  listEl.appendChild(row);
  if (atBottom) listEl.scrollTop = listEl.scrollHeight;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

/** Plain-text dump for the "Copy report" action. */
export function eventsAsText(): string {
  return events.map((e) => `[${formatTime(e.ts)}] ${e.cat}: ${e.msg}`).join('\n');
}
